import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

/**
 * Conservative default processing rate based on observed real-world data.
 * 72 pages took ~33 minutes ≈ 27.5s/page. Rounded up to 30s for a safe estimate.
 */
export const DEFAULT_SECONDS_PER_PAGE = 30;

type EstimateParams = {
  completedPages: number;
  totalPages: number;
  elapsedSeconds: number;
};

/**
 * Estimates remaining processing time based on observed completion rate.
 *
 * Uses completed pages and elapsed time to compute an adaptive rate,
 * then extrapolates how long the remaining pages will take.
 *
 * @returns Estimated remaining seconds, 0 if complete, or null if insufficient data.
 */
export const estimateRemainingSeconds = ({
  completedPages,
  totalPages,
  elapsedSeconds,
}: EstimateParams): number | null => {
  if (elapsedSeconds <= 0) {
    return null;
  }

  if (completedPages >= totalPages) {
    return 0;
  }

  if (completedPages <= 0) {
    const fallback = totalPages * DEFAULT_SECONDS_PER_PAGE - elapsedSeconds;
    return Math.max(0, Math.ceil(fallback));
  }

  const pagesPerSecond = completedPages / elapsedSeconds;
  const remainingPages = totalPages - completedPages;
  const remainingSeconds = Math.ceil(remainingPages / pagesPerSecond);

  return remainingSeconds;
};

/**
 * Formats a duration in seconds into a human-readable remaining time string.
 *
 * Examples: "~3m", "~1h 5m", "~45s"
 */
export const formatRemainingDuration = (
  seconds: number,
  locale: string
): string => {
  const totalSeconds = Math.max(1, Math.ceil(seconds));
  const d = dayjs.duration(totalSeconds, "seconds");

  const formatUnit = (
    unit: "hour" | "minute" | "second",
    value: number
  ): string => {
    return new Intl.NumberFormat(locale, {
      style: "unit",
      unit,
      unitDisplay: "narrow",
    }).format(value);
  };

  let hours = Math.floor(d.asHours());
  let minutes = Math.ceil(d.asMinutes() % 60);
  const secs = Math.max(1, Math.ceil(d.asSeconds() % 60));

  if (minutes === 60) {
    minutes = 0;
    hours += 1;
  }

  if (hours > 0) {
    return minutes > 0
      ? `~${formatUnit("hour", hours)} ${formatUnit("minute", minutes)}`
      : `~${formatUnit("hour", hours)}`;
  }

  if (d.asMinutes() >= 1) {
    return `~${formatUnit("minute", minutes)}`;
  }

  return `~${formatUnit("second", secs)}`;
};
